import Rollbar from 'rollbar/replay';

const env = process.env.REACT_APP_ENV;

/** Cross-origin scripts throw this exact message; the real error is hidden by the browser. */
function isCrossOriginScriptErrorMessage(msg) {
  return typeof msg === 'string' && /^script error\.?$/i.test(msg.trim());
}

function itemHasCrossOriginScriptError(item) {
  const body = item?.body;
  if (!body) return false;

  const fromException = (ex) => isCrossOriginScriptErrorMessage(ex?.message);

  if (fromException(body.trace?.exception)) return true;
  if (Array.isArray(body.trace_chain)) {
    for (const t of body.trace_chain) {
      if (fromException(t?.exception)) return true;
    }
  }
  const messageText =
    typeof body.message === 'string' ? body.message : body.message?.body;
  if (isCrossOriginScriptErrorMessage(messageText)) return true;

  return false;
}

/** Share of cross-origin "Script error." events still sent to Rollbar (rest dropped). */
const CROSS_ORIGIN_SCRIPT_ERROR_REPORT_RATE = 0.1;

const rollbarConfig = {
  accessToken: process.env.REACT_APP_ROLLBAR_ACCESS_TOKEN,
  environment: env,
  enabled: env === 'dev' ? false : true,
  captureUncaught: true,
  captureUnhandledRejections: true,
  checkIgnore(isUncaught, _args, item) {
    if (!itemHasCrossOriginScriptError(item)) return false;
    return Math.random() >= CROSS_ORIGIN_SCRIPT_ERROR_REPORT_RATE;
  },
  payload: {
    environment: env,
    client: {
      javascript: {
        source_map_enabled: true, // false by default
        // Optionally have Rollbar guess which frames the error was
        // thrown from when the browser does not provide line
        // and column numbers.
        guess_uncaught_frames: true,
      },
    },
  },
  // Session Replay Configuration
  replay: {
    enabled: env === 'dev' ? false : true, // Enable replay in production
    autoStart: true, // Start recording automatically
    triggers: [
      {
        type: 'occurrence',
        level: ['critical'],
        samplingRatio: 1.0, // Capture 100% of critical errors
      },
      {
        type: 'occurrence',
        level: ['error'],
        samplingRatio: 1.0, // Capture 80% of errors
      },
      {
        type: 'occurrence',
        level: ['warning'],
        samplingRatio: 1.0, // Capture 30% of warnings
      },
    ],
    triggerDefaults: {
      samplingRatio: 1.0,
      preDuration: 120, // Capture 60 seconds before the error
      postDuration: 60,  // Capture 5 seconds after the error
    },
    // Privacy Controls
    blockClass: 'rb-block',
    blockSelector: null, // Add selectors if needed, e.g., '.sensitive-data, #password-field'
    ignoreClass: 'rb-ignore',
    ignoreSelector: null, // Add selectors if needed
    maskTextClass: 'rb-mask',
    maskTextSelector: null, // Add selectors if needed
    maskAllInputs: false,
    maskInputOptions: {
      password: true, // Always mask password inputs
      email: false,    // Mask email inputs for privacy
      tel: false,
      text: false,
      color: false,
      date: false,
      'datetime-local': false,
      month: false,
      number: false,
      range: false,
      search: false,
      time: false,
      url: false,
      week: false,
    },
    // Advanced Options
    inlineStylesheet: true,
    inlineImages: false,
    collectFonts: true,
    slimDOMOptions: {
      script: true,
      comment: true,
      headFavicon: true,
      headWhitespace: true,
      headMetaDescKeywords: true,
      headMetaSocial: true,
      headMetaRobots: true,
      headMetaHttpEquiv: true,
      headMetaAuthorship: true,
      headMetaVerification: true,
    },
    // Debug Options (enable for troubleshooting)
    debug: {
      logEmits: env === 'dev', // Enable debug logging in dev only
      logErrors: true,
    },
  },
};

const rollbar = new Rollbar(rollbarConfig);

export default rollbar;
